import React, { useState, useEffect, useCallback, useRef } from "react";
import { FaArrowLeft, FaTrash, FaWindowMaximize } from "react-icons/fa";
import { BsChevronDown } from "react-icons/bs";
import { CiSettings } from "react-icons/ci";
import { FiSend, FiX } from "react-icons/fi";
import { Loader2 } from "lucide-react";
import { chatService } from "../../services/chatService";
import { AnimatePresence, motion } from "framer-motion";
import { VariableSizeList as List } from 'react-window';
import { RiCustomerService2Fill, RiRobot2Fill } from 'react-icons/ri';

const scrollbarStyles = `
  ::-webkit-scrollbar {
    width: 6px;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }
  ::-webkit-scrollbar-thumb {
    background-color: rgba(139, 92, 246, 0.2);
    border-radius: 3px;
  }
  ::-webkit-scrollbar-thumb:hover {
    background-color: rgba(139, 92, 246, 0.3);
  }
`;

// Add the styles to the document
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = scrollbarStyles;
  document.head.appendChild(style);
}

const ChatHeader = ({ 
  chatType, 
  onMinimize, 
  isMaximized, 
  setIsMaximized, 
  setShowSettingsMenu, 
  showSettingsMenu,
  clearChat,
  setChatType,
  setPage,
  loadMessages
}) => (
  <div className="relative py-4 px-4 flex items-center justify-between bg-gradient-to-r from-purple-900 via-purple-800 to-purple-900 border-b border-purple-700">
    {/* Left side */}
    <div className="flex items-center gap-3">
      {chatType ? (
        <button 
          onClick={() => setChatType(null)}
          className="flex items-center gap-2 p-2 hover:bg-purple-800/50 rounded-xl transition-all hover:scale-105 active:scale-95"
        >
          <FaArrowLeft size={14} className="text-purple-200" />
          <span className="text-sm text-purple-200">Back</span>
        </button>
      ) : (
        <button 
          onClick={onMinimize}
          className="p-2 hover:bg-purple-800/50 rounded-xl transition-all hover:scale-105 active:scale-95"
        >
          <BsChevronDown size={14} className="text-purple-200" />
        </button>
      )}
      
      {/* Chat type indicator removed */}
    </div>

    {/* Center title */}
    <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center">
      <h3 className="font-black text-2xl text-white uppercase tracking-wider relative"
          style={{
            textShadow: `
              0 0 10px rgba(255, 69, 0, 0.8),
              0 0 20px rgba(255, 69, 0, 0.8),
              0 0 30px rgba(255, 69, 0, 0.6),
              0 0 40px rgba(255, 69, 0, 0.4),
              2px 2px 2px rgba(0, 0, 0, 0.5)
            `,
            animation: 'flame 2s infinite'
          }}>
        <span className="relative">
          <span className="absolute -inset-1 blur-sm bg-gradient-to-b from-orange-500 via-red-500 to-purple-600 opacity-70 animate-pulse"></span>
          TCM
        </span>
        
      </h3>
    </div>

    <style>
      {`
        @keyframes flame {
          0%, 100% {
            text-shadow: 
              0 0 10px rgba(255, 69, 0, 0.8),
              0 0 20px rgba(255, 69, 0, 0.8),
              0 0 30px rgba(255, 69, 0, 0.6),
              0 0 40px rgba(255, 69, 0, 0.4),
              2px 2px 2px rgba(0, 0, 0, 0.5);
          }
          50% {
            text-shadow: 
              0 0 15px rgba(255, 69, 0, 0.9),
              0 0 25px rgba(255, 69, 0, 0.9),
              0 0 35px rgba(255, 69, 0, 0.7),
              0 0 45px rgba(255, 69, 0, 0.5),
              2px 2px 2px rgba(0, 0, 0, 0.5);
          }
        }
      `}
    </style>

    {/* Right side */}
    <div className="flex items-center gap-2">
      {chatType && (
        <>
          <button 
            onClick={() => setShowSettingsMenu(!showSettingsMenu)}
            className="p-2 hover:bg-purple-800/50 rounded-lg transition-all hover:scale-105 active:scale-95"
          >
            <CiSettings size={18} className="text-purple-200" />
          </button>
        </>
      )}
      <button 
        onClick={() => {
          setIsMaximized(!isMaximized);
          setPage(1);
          setTimeout(loadMessages, 0);
        }}
        className="p-2 hover:bg-purple-800/50 rounded-lg transition-all hover:scale-105 active:scale-95"
        title={isMaximized ? "Show recent messages" : "Show all messages"}
      >
        {isMaximized ? (
          <BsChevronDown size={14} className="text-purple-200" />
        ) : (
          <FaWindowMaximize size={14} className="text-purple-200" />
        )}
      </button>
    </div>

    {showSettingsMenu && (
      <div className="absolute right-0 top-full mt-2 w-48 rounded-xl bg-white shadow-lg border border-purple-100 py-1 z-50">
        <button
          onClick={clearChat}
          className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
        >
          <FaTrash size={12} />
          Clear Chat History
        </button>
      </div>
    )}
  </div>
);

const ChatTypeSelection = ({ onSelect }) => {
  const [isHovering, setIsHovering] = useState(false);
  const [previousChats, setPreviousChats] = useState([]);

  useEffect(() => {
    try {
      const storedMessages = localStorage.getItem('chat_messages');
      if (storedMessages) {
        const parsedMessages = JSON.parse(storedMessages);
        // Group messages by conversation
        const conversations = [];
        let currentConvo = [];
        parsedMessages.forEach(msg => {
          if (msg.role === 'assistant' && !currentConvo.length) {
            currentConvo = [msg];
          } else if (currentConvo.length) {
            currentConvo.push(msg);
          }
          if (currentConvo.length >= 2) {
            conversations.push([...currentConvo]);
            currentConvo = [];
          }
        });
        setPreviousChats(conversations);
      }
    } catch (error) {
      console.error('Error loading previous chats:', error);
    }
  }, []);

  return (
    <div 
      className="w-[380px] bg-white/10 rounded-xl overflow-hidden backdrop-blur-lg border border-purple-500/20 shadow-2xl"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="p-6 bg-gradient-to-br from-purple-600 to-purple-700 flex flex-col items-center">
        <div className="flex -space-x-3 mb-4">
          <div className="relative w-10 h-10 rounded-full overflow-hidden border-2 border-purple-300 transform hover:scale-105 transition-all">
            <RiRobot2Fill className="w-full h-full p-2 text-white" />
          </div>
          <div className="relative w-10 h-10 rounded-full overflow-hidden border-2 border-purple-300 transform hover:scale-105 transition-all">
            <RiCustomerService2Fill className="w-full h-full p-2 text-white" />
          </div>
        </div>
        <h3 className="text-white font-bold text-xl mb-1">How can we help?</h3>
        <p className="text-purple-100 text-sm text-center">Choose who you'd like to talk to</p>
      </div>

      <div className="p-5 space-y-6">
        <div className="bg-white rounded-lg p-4">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
            <p className="text-sm font-medium text-gray-600">Our team is ready to help</p>
          </div>

          <div className="grid grid-cols-1 gap-3">
            <button
              onClick={() => onSelect('ai')}
              className="flex items-center justify-center gap-2 bg-gradient-to-br from-purple-500 to-purple-600 text-white py-3 px-4 rounded-xl text-sm font-medium shadow-md hover:shadow-lg hover:from-purple-600 hover:to-purple-700 transition-all duration-300 group"
            >
              <RiRobot2Fill className="w-5 h-5 group-hover:scale-110 transition-transform" />
              <span>Chat with AI Assistant</span>
            </button>
            
            <button
              onClick={() => onSelect('human')}
              className="flex items-center justify-center gap-2 bg-white hover:bg-gray-50 text-purple-600 py-3 px-4 rounded-xl text-sm font-medium shadow-md hover:shadow-lg transition-all duration-300 group border-2 border-purple-200"
            >
              <RiCustomerService2Fill className="w-5 h-5 group-hover:scale-110 transition-transform" />
              <span>Talk to Support Team</span>
            </button>
          </div>
        </div>

        {previousChats.length > 0 && (
          <div className="bg-white/5 backdrop-blur-sm rounded-lg p-4">
            <h4 className="text-purple-100 font-medium mb-3 text-sm">Previous Conversations</h4>
            <div className="space-y-2 max-h-[200px] overflow-y-auto">
              {previousChats.slice(0, 5).map((convo, index) => (
                <button
                  key={index}
                  onClick={() => onSelect(convo[0].content.includes('AI Assistant') ? 'ai' : 'human', convo)}
                  className="w-full text-left p-3 rounded-lg bg-white/10 hover:bg-white/20 transition-colors group"
                >
                  <div className="flex items-center gap-2">
                    {convo[0].content.includes('AI Assistant') ? (
                      <RiRobot2Fill className="w-4 h-4 text-purple-300" />
                    ) : (
                      <RiCustomerService2Fill className="w-4 h-4 text-purple-300" />
                    )}
                    <span className="text-purple-100 text-sm truncate">
                      {convo[1]?.content.slice(0, 50)}...
                    </span>
                  </div>
                  <div className="text-[10px] text-purple-300 mt-1">
                    {new Date().toLocaleDateString()}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const formatMessageContent = (content) => {
  // Split content by code blocks
  const parts = content.split(/(```[\s\S]*?```)/g);
  
  return parts.map((part, index) => {
    // Check if this part is a code block
    if (part.startsWith('```')) {
      // Remove the backticks and optional language identifier
      const code = part.replace(/```(\w+)?\n?/, '').replace(/```$/, '');
      return (
        <pre key={index} className="mt-2 mb-2 rounded-lg bg-gray-900 p-3 overflow-x-auto">
          <code className="text-xs text-gray-100 font-mono">{code}</code>
        </pre>
      );
    }

    // Process inline code
    const inlineParts = part.split(/(`[^`]+`)/g);
    return (
      <span key={index}>
        {inlineParts.map((text, i) => {
          if (text.startsWith('`') && text.endsWith('`')) {
            // Inline code
            return (
              <code key={i} className="px-1.5 py-0.5 rounded-md bg-gray-800/10 font-mono text-[0.9em]">
                {text.slice(1, -1)}
              </code>
            );
          }
          // Regular text - handle line breaks and format text
          return text.split('\n').map((line, j, arr) => (
            <React.Fragment key={j}>
              {line
                .replace(/\*\*(.+?)\*\*/g, '§strong§$1§/strong§')
                .replace(/\*(.+?)\*/g, '§em§$1§/em§')
                .split(/(§\/?(?:strong|em)§)/g)
                .map((segment, k) => {
                  if (segment === '§strong§') return <strong key={k}></strong>;
                  if (segment === '§/strong§') return '';
                  if (segment === '§em§') return <em key={k}></em>;
                  if (segment === '§/em§') return '';
                  return segment;
                })}
              {j < arr.length - 1 && <br />}
            </React.Fragment>
          ))
        })}
      </span>
    );
  });
};

const ChatMessage = ({ message, isUser, chatType }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3 }}
    className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4 px-2`}
  >
    <div
      className={`max-w-[75%] rounded-2xl p-4 ${
        isUser 
          ? 'bg-gradient-to-r from-purple-600 to-purple-700 text-white shadow-purple-200' 
          : 'bg-white shadow-md border border-purple-100'
      } shadow-lg backdrop-blur-sm`}
    >
      <div className="text-sm leading-relaxed whitespace-pre-wrap">
        {!isUser && (
          <div className="text-xs font-medium text-purple-600 mb-1">
            {chatType === 'ai' ? 'AI Assistant' : 'Support Agent'}
          </div>
        )}
        {formatMessageContent(message.content)}
      </div>
      <div className={`text-[10px] mt-2 ${isUser ? 'text-purple-100' : 'text-gray-400'}`}>
        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  </motion.div>
);

const MessageRow = ({ index, style, data }) => {
  const { messages, getRowHeight, setRowHeight } = data;
  const message = messages[index];
  const rowRef = useRef(null);

  useEffect(() => {
    if (rowRef.current) {
      const height = rowRef.current.getBoundingClientRect().height;
      setRowHeight(index, height);
    }
  }, [message.content, index, setRowHeight]);

  return (
    <div ref={rowRef} style={{ ...style, height: 'auto' }}>
      <ChatMessage 
        message={message} 
        isUser={message.role === 'user'} 
      />
    </div>
  );
};

export default function SidebarChat({ isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [listHeight, setListHeight] = useState  (400);
  const [page, setPage] = useState(1);
  const [chatType, setChatType] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const messagesRef = useRef(null);
  const listRef = useRef(null);
  const rowHeights = useRef({});
  
  const MAX_STORED_MESSAGES = 1000;
  const MESSAGES_PER_PAGE = isMaximized ? MAX_STORED_MESSAGES : 5;

  const getRowHeight = index => rowHeights.current[index] || 100;

  const setRowHeight = (index, size) => {
    if (rowHeights.current[index] !== size) {
      rowHeights.current[index] = size;
      if (listRef.current) {
        listRef.current.resetAfterIndex(index);
      }
    }
  };

  const loadMessages = useCallback(() => {
    try {
      const storedMessages = localStorage.getItem('chat_messages');
      if (storedMessages) {
        const parsedMessages = JSON.parse(storedMessages);
        // When maximized, show all messages. Otherwise, show last 5
        if (isMaximized) {
          setMessages(parsedMessages);
        } else {
          setMessages(parsedMessages.slice(-5));
        }
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    }
  }, [isMaximized]);

  const saveMessages = useCallback((newMessages) => {
    try {
      // Keep only the most recent MAX_STORED_MESSAGES messages
      const messagesToStore = newMessages.slice(-MAX_STORED_MESSAGES);
      localStorage.setItem('chat_messages', JSON.stringify(messagesToStore));
    } catch (error) {
      console.error('Error saving messages:', error);
    }
  }, []);

  // Load messages when component mounts or page changes
  useEffect(() => {
    loadMessages();
  }, [loadMessages, page]);

  // Initialize chat when sidebar opens
  useEffect(() => {
    const initializeChat = async () => {
      if (isOpen && !isInitialized) {
        try {
          setIsLoading(true);
          await chatService.startChat();
          const welcomeMessage = {
            role: 'assistant',
            content: 'Hi! I\'m Eureka TCM, your campus management assistant. How can I help you today?'
          };
          setMessages(prev => {
            const newMessages = [...prev, welcomeMessage];
            saveMessages(newMessages);
            return newMessages;
          });
          setIsInitialized(true);
        } catch (error) {
          console.error('Error initializing chat:', error);
          const errorMessage = {
            role: 'assistant',
            content: 'I\'m having trouble connecting. Please try refreshing the page or contact support if the issue persists.'
          };
          setMessages(prev => {
            const newMessages = [...prev, errorMessage];
            saveMessages(newMessages);
            return newMessages;
          });
        } finally {
          setIsLoading(false);
        }
      }
    };

    initializeChat();
  }, [isOpen, isInitialized, saveMessages]);

  // Update list height on container resize
  useEffect(() => {
    const updateListHeight = () => {
      if (messagesRef.current) {
        setListHeight(messagesRef.current.offsetHeight);
      }
    };

    updateListHeight();
    window.addEventListener('resize', updateListHeight);
    return () => window.removeEventListener('resize', updateListHeight);
  }, []);

  // Reset scroll position when new messages arrive
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollToItem(messages.length - 1);
    }
  }, [messages.length]);

  // Handle loading more messages when scrolling to top
  const handleScroll = useCallback((e) => {
    const element = e.target;
    if (element.scrollTop === 0) {
      setPage(prev => prev + 1);
    }
  }, []);

  useEffect(() => {
    const chatContainer = document.getElementById('chat-messages');
    if (chatContainer) {
      chatContainer.addEventListener('scroll', handleScroll);
      return () => chatContainer.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // Update handleSendMessage to include persistence
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() || isLoading) return;

    try {
      setIsLoading(true);
      const userMessage = { role: 'user', content: newMessage.trim() };
      setMessages(prev => {
        const newMessages = [...prev, userMessage];
        saveMessages(newMessages);
        return newMessages;
      });
      setNewMessage("");

      const tempMessage = { role: 'assistant', content: '' };
      setMessages(prev => {
        const newMessages = [...prev, tempMessage];
        saveMessages(newMessages);
        return newMessages;
      });

      await chatService.sendMessage(
        newMessage.trim(), 
        null, 
        0,
        (chunk, done) => {
          setMessages(prev => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage.role === 'assistant') {
              if (done) {
                lastMessage.content = chunk;
              } else {
                lastMessage.content += chunk;
              }
              saveMessages(newMessages);
            }
            return newMessages;
          });
          
          if (done) {
            setIsLoading(false);
          }
        }
      );
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: error.message || "Sorry, I'm having trouble responding right now. Please try again."
      };
      setMessages(prev => {
        const newMessages = [...prev, errorMessage];
        saveMessages(newMessages);
        return newMessages;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChatTypeSelect = async (type, previousConversation = null) => {
    setChatType(type);
    setIsLoading(true);
    
    try {
      await chatService.startChat();
      if (previousConversation) {
        setMessages(previousConversation);
        saveMessages(previousConversation);
      } else {
        const welcomeMessage = {
          role: 'assistant',
          content: type === 'ai' 
            ? 'Hi! I\'m Eureka TCM, your campus management assistant. How can I help you today?'
            : 'Connecting you with a support agent. Please note that response times may vary during peak hours.'
        };
        setMessages([welcomeMessage]);
        saveMessages([welcomeMessage]);
      }
      setIsInitialized(true);
    } catch (error) {
      console.error('Error initializing chat:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'I\'m having trouble connecting. Please try refreshing the page or contact support if the issue persists.'
      };
      setMessages([errorMessage]);
      saveMessages([errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = useCallback(() => {
    try {
      localStorage.removeItem('chat_messages');
      setMessages([]);
      setIsInitialized(false);
      setPage(1);
      setChatType(null);
      setShowSettingsMenu(false);
    } catch (error) {
      console.error('Error clearing chat:', error);
    }
  }, []);

  return (
    <motion.div 
      initial={{ x: '100%', opacity: 0 }}
      animate={{ 
        x: isOpen ? 0 : '100%',
        opacity: isOpen ? 1 : 0,
        height: isMaximized ? '100vh' : chatType ? '600px' : 'auto',
        width: chatType ? '380px' : 'auto'
      }}
      transition={{ type: "spring", damping: 30, stiffness: 400 }}
      style={{
        position: 'fixed',
        right: '20px',
        bottom: isMaximized ? 0 : '20px',
        transformOrigin: 'bottom right'
      }}
      className="bg-gradient-to-b from-gray-900 to-purple-900 backdrop-blur-md border border-purple-700 
        rounded-xl shadow-[0_0_40px_rgba(147,51,234,0.3)] flex flex-col z-50 overflow-hidden"
    >
      <ChatHeader 
        chatType={chatType}
        onMinimize={() => chatType ? setChatType(null) : onClose()}
        isMaximized={isMaximized}
        setIsMaximized={setIsMaximized}
        setShowSettingsMenu={setShowSettingsMenu}
        showSettingsMenu={showSettingsMenu}
        clearChat={clearChat}
        setChatType={setChatType}
        setPage={setPage}
        loadMessages={loadMessages}
      />

      {!chatType ? (
        <ChatTypeSelection onSelect={handleChatTypeSelect} />
      ) : (
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: 'auto' }}
          transition={{ type: "spring", damping: 30, stiffness: 400 }}
          className="flex flex-col flex-grow relative"
          style={{ height: isMaximized ? 'calc(100vh - 64px)' : '536px' }}
        >
          {/* Messages */}
          <div 
            ref={messagesRef}
            id="chat-messages"
            className="flex-1 overflow-y-auto p-4 bg-gradient-to-b from-gray-900 to-purple-900"
            style={{ height: isMaximized ? 'calc(100vh - 140px)' : '460px' }}
          >
            <AnimatePresence>
              <List
                ref={listRef}
                height={isMaximized ? window.innerHeight - 140 : 460}
                itemCount={messages.length}
                itemSize={getRowHeight}
                width="100%"
                itemData={{ 
                  messages, 
                  getRowHeight, 
                  setRowHeight,
                  chatType 
                }}
              >
                {MessageRow}
              </List>
            </AnimatePresence>
          </div>

          {/* Input */}
          <div className="absolute bottom-0 left-0 right-0 pb-3 px-4 bg-gradient-to-t from-gray-900 to-transparent">
            <form 
              onSubmit={handleSendMessage}
              className="flex gap-2 transform translate-y-[-5px]"
            >
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 bg-gray-800 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 border border-purple-700"
                disabled={isLoading}
              />
              <button
                type="submit"
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {isLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <FiSend className="w-5 h-5" />
                )}
              </button>
            </form>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
